import { INewbuildingMapPin } from '../../../types/map';
import { ICoordinates } from '../../../types/newbuildingsCompare';
import { TNewbuildingsMapPlatform } from '../NewbuildingsMap';

const TILE_SIZE = 256;
const DESKTOP_CLUSTER_DISTANCE = 64;
const MOBILE_CLUSTER_DISTANCE = 96;

export interface IPinCluster {
  /** Стабильный ключ для React: id всех ЖК кластера. */
  id: string;
  /** Точка, в которой рисуется кластер, — среднее по его пинам. */
  coordinates: ICoordinates;
  pins: INewbuildingMapPin[];
}

function toWorldPixel({ lat, lng }: ICoordinates, zoom: number): { x: number; y: number } {
  const scale = TILE_SIZE * 2 ** zoom;
  const sinLat = Math.sin((lat * Math.PI) / 180);

  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
  };
}

function getCenter(pins: INewbuildingMapPin[]): ICoordinates {
  const sum = pins.reduce((acc, pin) => ({ lat: acc.lat + pin.coordinates.lat, lng: acc.lng + pin.coordinates.lng }), {
    lat: 0,
    lng: 0,
  });

  return { lat: sum.lat / pins.length, lng: sum.lng / pins.length };
}

export function clusterPins(
  pins: INewbuildingMapPin[],
  zoom: number | null,
  platform?: TNewbuildingsMapPlatform,
): IPinCluster[] {
  if (zoom === null) {
    return pins.map(pin => ({ id: String(pin.newbuilding.id), coordinates: pin.coordinates, pins: [pin] }));
  }

  const points = pins.map(pin => ({ pin, ...toWorldPixel(pin.coordinates, zoom) }));
  const used = new Set<number>();
  const clusters: IPinCluster[] = [];

  points.forEach((point, index) => {
    if (used.has(index)) {
      return;
    }

    used.add(index);
    const group = [point.pin];

    points.forEach((candidate, candidateIndex) => {
      if (used.has(candidateIndex)) {
        return;
      }

      const distance = Math.hypot(candidate.x - point.x, candidate.y - point.y);

      const clusterDistance = platform === 'desktop' ? DESKTOP_CLUSTER_DISTANCE : MOBILE_CLUSTER_DISTANCE;

      if (distance < clusterDistance) {
        used.add(candidateIndex);
        group.push(candidate.pin);
      }
    });

    clusters.push({
      id: group.map(pin => pin.newbuilding.id).join('-'),
      coordinates: group.length === 1 ? group[0].coordinates : getCenter(group),
      pins: group,
    });
  });

  return clusters;
}
